/**
 * PlayBear Creator Email Bridge v2.1.0
 *
 * Supports:
 * - Klaviyo sign-up form submission
 * - Legacy SendWILL subscription observation
 *
 * Purpose:
 * - Never interfere with Klaviyo or SendWILL submission.
 * - Hash email in-browser.
 * - Bind email hash to Creator touches captured by PB Attribution V1.
 *
 * Privacy:
 * - Plaintext email is NOT sent to PlayBear attribution backend.
 *
 * Installation:
 * - Load AFTER pb-creator-attribution.js
 */
(function () {
  'use strict';

  const VERSION = '2.1.0';

  const BIND_ENDPOINT =
    'https://playbear-creator-attribution-v2.rough-cell-5947.workers.dev/v2/bind-email';

  const MAX_TOUCHES = 5;

  // Klaviyo — PB BR Welcome 10%
  const KLAVIYO_FORM_IDS = new Set([
    'R8P3nM'
  ]);

  // Legacy SendWILL support during migration
  const SENDWILL_HOST = 'ecomsend-go.ecomsend.com';
  const SENDWILL_SUBSCRIBE_PATH =
    '/api/shop/v2/customer/subscribe';

  const state = {
    version: VERSION,
    installed: false,
    lastEvent: null
  };

  function isConfigured() {
    return !BIND_ENDPOINT.includes(
      'REPLACE_WITH_YOUR_WORKER'
    );
  }

  function cleanText(value, maxLength) {
    const text = String(value || '').trim();

    return text.length > maxLength
      ? text.slice(0, maxLength)
      : text;
  }

  function normalizeEmail(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  function isReasonableEmail(value) {
    return (
      value.length >= 3 &&
      value.length <= 320 &&
      value.includes('@')
    );
  }

  async function sha256Hex(value) {
    const bytes =
      new TextEncoder().encode(value);

    const digest =
      await crypto.subtle.digest(
        'SHA-256',
        bytes
      );

    return Array.from(
      new Uint8Array(digest)
    )
      .map((byte) =>
        byte.toString(16).padStart(2, '0')
      )
      .join('');
  }

  function readPbAttributionState() {
    try {
      if (
        window.PlayBearCreatorAttribution &&
        typeof window
          .PlayBearCreatorAttribution
          .read === 'function'
      ) {
        return window
          .PlayBearCreatorAttribution
          .read();
      }
    } catch (_) {
      // Fall through.
    }

    try {
      const raw =
        localStorage.getItem(
          'pb_creator_attribution_v1'
        );

      return raw
        ? JSON.parse(raw)
        : null;
    } catch (_) {
      return null;
    }
  }

  /**
   * Supports both:
   *
   * Current V1:
   *   ref
   *   coupon
   *
   * Legacy Bridge structure:
   *   creator_ref
   *   creator_coupon
   */
  function buildTouches(pbState) {
    if (
      !pbState ||
      !Array.isArray(pbState.touches)
    ) {
      return [];
    }

    return pbState.touches
      .slice(-MAX_TOUCHES)
      .map((touch) => {
        if (!touch) {
          return null;
        }

        const creatorRef =
          touch.creator_ref ||
          touch.ref ||
          touch.creator_coupon ||
          touch.coupon ||
          '';

        if (!creatorRef) {
          return null;
        }

        const creatorCoupon =
          touch.creator_coupon ||
          touch.coupon ||
          creatorRef;

        return {
          creator_ref:
            cleanText(
              creatorRef,
              64
            ).toUpperCase(),

          creator_coupon:
            cleanText(
              creatorCoupon,
              100
            ).toUpperCase(),

          source:
            cleanText(
              touch.source,
              120
            ).toLowerCase(),

          medium:
            cleanText(
              touch.medium,
              80
            ).toLowerCase(),

          campaign:
            cleanText(
              touch.campaign,
              160
            ).toLowerCase(),

          content:
            cleanText(
              touch.content,
              160
            ).toLowerCase(),

          first_seen_at:
            cleanText(
              touch.first_seen_at ||
              touch.seen_at,
              40
            ),

          last_seen_at:
            cleanText(
              touch.last_seen_at ||
              touch.seen_at ||
              touch.first_seen_at,
              40
            )
        };
      })
      .filter(Boolean);
  }

  async function postBinding(payload) {
    const response =
      await fetch(
        BIND_ENDPOINT,
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',

            'X-PB-Bridge-Version':
              VERSION
          },

          body:
            JSON.stringify(payload),

          keepalive: true
        }
      );

    if (!response.ok) {
      throw new Error(
        'PB bind-email failed with HTTP ' +
        response.status
      );
    }

    return response.json();
  }

  /**
   * Common binding path.
   *
   * Klaviyo and SendWILL both end here.
   */
  async function bindEmail(
    emailValue,
    options
  ) {
    const opts = options || {};

    const source =
      cleanText(
        opts.source || '',
        50
      );

    const popupId =
      cleanText(
        opts.popupId || '',
        100
      );

    const reason =
      cleanText(
        opts.reason ||
        'subscribe_success',
        100
      );

    const email =
      normalizeEmail(emailValue);

    if (!isReasonableEmail(email)) {
      state.lastEvent = {
        at:
          new Date().toISOString(),

        status:
          'ignored',

        source:
          source,

        reason:
          'email_missing_or_obviously_invalid',

        popup_id:
          popupId
      };

      return;
    }

    const pbState =
      readPbAttributionState();

    const touches =
      buildTouches(pbState);

    if (!touches.length) {
      state.lastEvent = {
        at:
          new Date().toISOString(),

        status:
          'ignored',

        source:
          source,

        reason:
          'no_creator_touch',

        popup_id:
          popupId
      };

      return;
    }

    if (!isConfigured()) {
      state.lastEvent = {
        at:
          new Date().toISOString(),

        status:
          'configuration_required',

        source:
          source,

        reason:
          'bind_endpoint_not_configured',

        popup_id:
          popupId,

        creator_refs:
          touches.map(
            (touch) =>
              touch.creator_ref
          )
      };

      console.warn(
        '[PB Email Bridge] ' +
        'Configure BIND_ENDPOINT ' +
        'before production use.'
      );

      return;
    }

    const emailHash =
      await sha256Hex(email);

    const bindingPayload = {
      email_hash:
        emailHash,

      attribution_id:
        cleanText(
          pbState &&
          pbState.attribution_id,
          100
        ),

      popup_id:
        popupId,

      bind_reason:
        reason,

      observed_at:
        new Date().toISOString(),

      touches:
        touches
    };

    try {
      const result =
        await postBinding(
          bindingPayload
        );

      state.lastEvent = {
        at:
          new Date().toISOString(),

        status:
          'bound',

        source:
          source,

        reason:
          reason,

        popup_id:
          popupId,

        creator_refs:
          touches.map(
            (touch) =>
              touch.creator_ref
          ),

        backend_status:
          result &&
          result.status
      };
    } catch (error) {
      state.lastEvent = {
        at:
          new Date().toISOString(),

        status:
          'backend_error',

        source:
          source,

        reason:
          reason,

        popup_id:
          popupId,

        creator_refs:
          touches.map(
            (touch) =>
              touch.creator_ref
          ),

        message:
          String(
            error &&
            error.message ||
            error
          )
      };

      console.warn(
        '[PB Email Bridge] ' +
        'Binding failed:',
        error
      );
    }
  }

  /* =========================================
     KLAVIYO
     ========================================= */

  function extractKlaviyoEmail(detail) {
    const metadata =
      detail &&
      (
        detail.metaData ||
        detail.metadata
      ) ||
      {};

    return (
      metadata.$email ||
      metadata.email ||
      (detail && detail.$email) ||
      (detail && detail.email) ||
      ''
    );
  }

  function installKlaviyoObserver() {
    window.addEventListener(
      'klaviyoForms',
      function (event) {
        const detail =
          event &&
          event.detail
            ? event.detail
            : {};

        const type =
          String(
            detail.type || ''
          ).toLowerCase();

        if (type !== 'submit') {
          return;
        }

        const formId =
          cleanText(
            detail.formId ||
            detail.form_id ||
            '',
            100
          );

        if (
          !KLAVIYO_FORM_IDS.has(
            formId
          )
        ) {
          return;
        }

        const email =
          extractKlaviyoEmail(
            detail
          );

        Promise.resolve(
          bindEmail(
            email,
            {
              source:
                'klaviyo',

              popupId:
                formId,

              // Keep the existing backend-compatible
              // reason value.
              reason:
                'subscribe_success'
            }
          )
        ).catch((error) => {
          state.lastEvent = {
            at:
              new Date().toISOString(),

            status:
              'observer_error',

            source:
              'klaviyo',

            popup_id:
              formId,

            message:
              String(
                error &&
                error.message ||
                error
              )
          };

          console.warn(
            '[PB Email Bridge] ' +
            'Klaviyo observer failed:',
            error
          );
        });
      }
    );
  }

  /* =========================================
     LEGACY SENDWILL SUPPORT
     ========================================= */

  function safeUrl(value) {
    try {
      if (value instanceof URL) {
        return value;
      }

      return new URL(
        String(value),
        window.location.href
      );
    } catch (_) {
      return null;
    }
  }

  function isSendwillSubscribe(
    urlValue,
    methodValue
  ) {
    const url =
      safeUrl(urlValue);

    const method =
      String(
        methodValue || 'GET'
      ).toUpperCase();

    return Boolean(
      url &&
      method === 'POST' &&
      url.hostname ===
        SENDWILL_HOST &&
      url.pathname ===
        SENDWILL_SUBSCRIBE_PATH
    );
  }

  function getValidationMessages(
    responseJson
  ) {
    const results =
      responseJson &&
      responseJson.data &&
      responseJson.data
        .validate_results;

    if (
      !results ||
      typeof results !== 'object'
    ) {
      return [];
    }

    return Object.values(
      results
    ).map(
      (value) =>
        String(value || '')
    );
  }

  function classifySendwillResponse(
    responseJson
  ) {
    if (
      responseJson &&
      responseJson.data &&
      responseJson.data
        .is_success === true
    ) {
      return {
        bind: true,
        reason:
          'subscribe_success'
      };
    }

    const messages =
      getValidationMessages(
        responseJson
      );

    if (
      messages.some(
        (message) =>
          /already subscribed with this email/i
            .test(message)
      )
    ) {
      return {
        bind: true,
        reason:
          'already_subscribed_email'
      };
    }

    return {
      bind: false,
      reason:
        'not_valid_for_binding'
    };
  }

  async function handleSendwillSubmission(
    requestPayload,
    responseJson
  ) {
    const classification =
      classifySendwillResponse(
        responseJson
      );

    if (!classification.bind) {
      state.lastEvent = {
        at:
          new Date().toISOString(),

        status:
          'ignored',

        source:
          'sendwill',

        reason:
          classification.reason
      };

      return;
    }

    const email =
      normalizeEmail(
        requestPayload &&
        requestPayload.email
      );

    const popupId =
      cleanText(
        requestPayload &&
        requestPayload.popup_id,
        100
      );

    return bindEmail(
      email,
      {
        source:
          'sendwill',

        popupId:
          popupId,

        reason:
          classification.reason
      }
    );
  }

  async function parseJsonBodyFromFetch(
    input,
    init
  ) {
    try {
      if (
        init &&
        typeof init.body ===
          'string'
      ) {
        return JSON.parse(
          init.body
        );
      }

      if (
        typeof Request !==
          'undefined' &&
        input instanceof Request
      ) {
        const clone =
          input.clone();

        return await clone.json();
      }
    } catch (_) {
      return null;
    }

    return null;
  }

  function installFetchObserver() {
    if (
      typeof window.fetch !==
      'function'
    ) {
      return;
    }

    const originalFetch =
      window.fetch;

    window.fetch =
      async function pbObservedFetch(
        input,
        init
      ) {
        const url =
          typeof input === 'string'
            ? input
            : input &&
              input.url;

        const method =
          (
            init &&
            init.method
          ) ||
          (
            input &&
            input.method
          ) ||
          'GET';

        const shouldObserve =
          isSendwillSubscribe(
            url,
            method
          );

        const payloadPromise =
          shouldObserve
            ? parseJsonBodyFromFetch(
                input,
                init
              )
            : null;

        const response =
          await originalFetch.apply(
            this,
            arguments
          );

        if (shouldObserve) {
          const responseClone =
            response.clone();

          Promise.all([
            payloadPromise,
            responseClone.json()
          ])
            .then(
              ([
                requestPayload,
                responseJson
              ]) =>
                handleSendwillSubmission(
                  requestPayload,
                  responseJson
                )
            )
            .catch(
              (error) => {
                state.lastEvent = {
                  at:
                    new Date()
                      .toISOString(),

                  status:
                    'observer_error',

                  source:
                    'sendwill',

                  message:
                    String(
                      error &&
                      error.message ||
                      error
                    )
                };

                console.warn(
                  '[PB Email Bridge] ' +
                  'Fetch observer failed:',
                  error
                );
              }
            );
        }

        return response;
      };
  }

  function installXhrObserver() {
    if (
      typeof XMLHttpRequest ===
      'undefined'
    ) {
      return;
    }

    const originalOpen =
      XMLHttpRequest
        .prototype
        .open;

    const originalSend =
      XMLHttpRequest
        .prototype
        .send;

    XMLHttpRequest
      .prototype
      .open =
      function pbObservedXhrOpen(
        method,
        url
      ) {
        this.__pbObservedMethod =
          method;

        this.__pbObservedUrl =
          url;

        return originalOpen.apply(
          this,
          arguments
        );
      };

    XMLHttpRequest
      .prototype
      .send =
      function pbObservedXhrSend(
        body
      ) {
        const shouldObserve =
          isSendwillSubscribe(
            this.__pbObservedUrl,
            this.__pbObservedMethod
          );

        if (shouldObserve) {
          let requestPayload =
            null;

          try {
            if (
              typeof body ===
              'string'
            ) {
              requestPayload =
                JSON.parse(body);
            }
          } catch (_) {
            requestPayload =
              null;
          }

          this.addEventListener(
            'load',
            () => {
              try {
                const responseJson =
                  JSON.parse(
                    this.responseText ||
                    '{}'
                  );

                Promise.resolve(
                  handleSendwillSubmission(
                    requestPayload,
                    responseJson
                  )
                ).catch(
                  (error) => {
                    console.warn(
                      '[PB Email Bridge] ' +
                      'XHR observer failed:',
                      error
                    );
                  }
                );
              } catch (error) {
                console.warn(
                  '[PB Email Bridge] ' +
                  'XHR response parse failed:',
                  error
                );
              }
            }
          );
        }

        return originalSend.apply(
          this,
          arguments
        );
      };
  }

  /* =========================================
     INSTALL
     ========================================= */

  installKlaviyoObserver();

  // Temporary compatibility during migration.
  installFetchObserver();
  installXhrObserver();

  state.installed = true;

  window.PlayBearCreatorEmailBridge = {
    version:
      VERSION,

    status:
      function () {
        return {
          version:
            state.version,

          installed:
            state.installed,

          configured:
            isConfigured(),

          bindEndpoint:
            isConfigured()
              ? BIND_ENDPOINT
              : 'NOT_CONFIGURED',

          klaviyoFormIds:
            Array.from(
              KLAVIYO_FORM_IDS
            ),

          lastEvent:
            state.lastEvent
        };
      }
  };
})();